import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Sidebar } from 'primeng/sidebar';
import { FidelityCardTypeEnum } from 'src/app/services/fidelity/fidelity';
import { FidelityService } from 'src/app/services/fidelity/fidelity.service';
import { Subscribable } from '../../stores/tunnel/model/subscribable';
import { CustomerState } from '../../stores/tunnel/model/customer-state';
import { ToastService } from '../../services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { MODAL, ModalService } from '../../services/modal.service';
import { TunnelDataStore } from '../../stores/tunnel/tunnel-data.store';
import { PaytweakRequestInput } from '../../services/fidelity/paytweak';
import { dateToString } from '../../services/helpers/utils';
import { logError } from 'src/app/services/helpers/sentry.helper';
import { FeatureSettingsService } from '../../services/feature-settings/feature-settings.service';

@Component({
  selector: 'ng-casto-fidelity-info',
  templateUrl: './fidelity-info.component.html',
})
export class FidelityInfoComponent extends Subscribable implements OnInit, OnDestroy {
  @Output() onClose: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild('allOffers') allOffersSidebar!: Sidebar;

  protected isLoading: boolean = false;
  protected minPointValue: number = 0;

  protected currentCustomer: CustomerState;

  get isActive(): boolean {
    return (
      this.currentCustomer?.fidelity?.typeCard !== undefined &&
      this.currentCustomer.fidelity.typeCard !== FidelityCardTypeEnum.PREMIUMWAITING &&
      this.currentCustomer.fidelity.typeCard !== FidelityCardTypeEnum.AUCUNE
    );
  }

  constructor(
    private readonly toastService: ToastService,
    private readonly translateService: TranslateService,
    private readonly modalService: ModalService,
    private readonly fidelityService: FidelityService,
    private readonly tunnelDataStore: TunnelDataStore,
    private readonly featureSettingsService: FeatureSettingsService
  ) {
    super();
  }

  ngOnInit(): void {
    super.subscribe();
  }

  ngOnDestroy(): void {
    super.unsubscribe();
  }

  protected subscribeToObservables(): void {
    this.subscriptions.push(
      this.tunnelDataStore.getCurrentCustomer().subscribe(customer => {
        if (customer) {
          this.currentCustomer = customer;
        }
      })
    );
  }

  protected displayRenewBtn(): boolean {
    if (!this.currentCustomer.fidelity?.expiredDate) return false;
    let today = new Date();
    let dateToNotice = new Date();

    const nbDaysNoticeEndSubscriptionPaidCard = 30;
    //Ajout de la date de notification en fonction du setting
    dateToNotice.setTime(
      this.currentCustomer.fidelity.expiredDate.getTime() - nbDaysNoticeEndSubscriptionPaidCard * 24 * 60 * 60 * 1000
    );
    return today > dateToNotice;
  }

  protected async postRenewPremiumCard() {
    if (!(await this.featureSettingsService.isFidelityEnabled())) {
      return;
    }

    try {
      await this.fidelityService.postRenewPremiumCard({
        idKingfisher: this.currentCustomer.userData.kfId,
      });

      await this.showPaytweakQrCode();
    } catch (error) {
      this.toastService.showErrorToast(this.translateService.instant('popins.signup.error_title'), error.message);
      logError(error);
    } finally {
      this.isLoading = false;
    }
  }

  private buildPaytweakRequestInput(): PaytweakRequestInput {
    return {
      customerId: this.currentCustomer.userData.kfId,
      cardNumber: this.currentCustomer.fidelity.cardNumber,
      firstname: this.currentCustomer.userData.firstname,
      lastname: this.currentCustomer.userData.lastname,
      address: this.currentCustomer.userData.address.line1,
      postalCode: this.currentCustomer.userData.address.postalCode,
      city: this.currentCustomer.userData.address.city,
      country: this.currentCustomer.userData.address.country,
      phone: this.currentCustomer.userData.phoneNumber,
      email: this.currentCustomer.userData.email,
    };
  }

  private async showPaytweakQrCode() {
    if (!(await this.featureSettingsService.isFidelityEnabled())) {
      return;
    }

    let input: PaytweakRequestInput = this.buildPaytweakRequestInput();

    this.modalService.display(MODAL.FIDELITY_PAYMENT_QRCODE, {
      paytweakRequestInput: input,
      onCloseCallback: undefined,
    });
  }

  //Permet d'ajuster la position de la bulle avec les points
  protected getSliderPosition(): number {
    const offset = 2;
    let val: number = (this.currentCustomer.fidelity.points / this.currentCustomer.fidelity.maxPoints) * 100 - offset;
    if (val + offset < 0) {
      val = -offset;
    } else if (val + offset > 89) {
      val = 89;
    }
    return val;
  }

  protected readonly FidelityCardTypeEnum = FidelityCardTypeEnum;

  close() {
    this.onClose.emit();
  }

  protected readonly dateToString = dateToString;
}
